/* eslint-disable no-console */
import { get, set, del } from 'idb-keyval'
import { captureException, withScope } from '@sentry/react'
import { QueryKey } from '@tanstack/react-query'
import {
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client'

export const IDB_VALID_KEY_PREFIX = 'reactQuery'

type IDBOperation = 'persist' | 'restore' | 'remove'

// Neither means the saved data is bad, so keep it. Chrome closes an IndexedDB
// transaction that sits idle for 60 seconds, usually because the computer went
// to sleep part-way through a write.
const SKIP_AND_REPORT_ERROR_NAMES = ['TimeoutError', 'AbortError']

const SKIP_AND_WARN_ERROR_NAMES = ['DataCloneError']

const CLEAR_ERROR_NAMES = [
  'UnknownError',
  'QuotaExceededError',
  'InvalidStateError',
  'DataError',
]

export function createIDBPersister() {
  const combinedKey = `${IDB_VALID_KEY_PREFIX}-${
    import.meta.env.VITE_ENVIRONMENT
  }`

  function warn(err: unknown, operation: IDBOperation, disposition: string) {
    console.warn(`IndexedDB ${operation} ${disposition}:`, combinedKey, err)
  }

  function report(err: unknown, operation: IDBOperation, disposition: string) {
    withScope((scope) => {
      scope.setTag('idb_operation', operation)
      scope.setTag('idb_disposition', disposition)
      scope.setContext('indexeddb', {
        operation,
        disposition,
        key: combinedKey,
        visibilityState:
          typeof document === 'undefined'
            ? 'unknown'
            : document.visibilityState,
      })
      captureException(err)
    })
  }

  /**
   * Runs one IndexedDB operation and never throws.
   *
   * React Query calls the save function without watching for failures, so
   * anything thrown here becomes an unhandled promise rejection.
   */
  async function safe<T>(
    operation: Exclude<IDBOperation, 'remove'>,
    fn: () => Promise<T | undefined>,
  ): Promise<T | undefined> {
    try {
      return await fn()
    } catch (err) {
      const name = err instanceof DOMException ? err.name : undefined

      if (name && SKIP_AND_REPORT_ERROR_NAMES.includes(name)) {
        report(err, operation, 'skipped')
        return
      }

      if (name && SKIP_AND_WARN_ERROR_NAMES.includes(name)) {
        warn(err, operation, 'skipped')
        return
      }

      if (name && CLEAR_ERROR_NAMES.includes(name)) {
        warn(err, operation, 'cleared')
        try {
          await del(combinedKey)
        } catch (cleanupErr) {
          warn(cleanupErr, operation, 'clear-failed')
        }
        return
      }

      report(err, operation, 'unhandled')
      return
    }
  }

  return {
    persistClient: (client: PersistedClient) =>
      safe('persist', () => set(combinedKey, client)),
    restoreClient: () =>
      safe('restore', () => get<PersistedClient>(combinedKey)),
    removeClient: async () => {
      try {
        await del(combinedKey)
      } catch (err) {
        warn(err, 'remove', 'failed')
      }
    },
  } satisfies Persister
}

export const PERSISTED_TIME = 1000 * 60 * 60 * 12 // 12 hours

export function isQueryPersisted(queryKey: QueryKey): boolean {
  const lastElement = queryKey[queryKey.length - 1]
  if (
    lastElement &&
    typeof lastElement === 'object' &&
    'persist' in lastElement
  )
    return !!lastElement.persist

  return false
}
